import React, { useEffect, useRef, useState } from "react";
import { useCreateAgent } from "../../hooks/useCreateAgent";
import AgentSettingsTab from "../../components/tabs/AgentSettingsTab";
import AgentKnowledgeTab from "../../components/tabs/AgentKnowledgeTab";
import AgentTryAITab from "../../components/tabs/AgentTryAITab";
import DeleteConfirmationModal from "../../components/modals/DeleteConfirmationModal";
import {
  ArrowLeft,
  Settings,
  Brain,
  MessageSquare,
  Trash2,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

const AddAgentPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Use ref to track initialization to prevent loops
  const isInitialized = useRef(false);

  // Get agent ID from URL for edit mode
  const agentId = searchParams.get("id");

  const {
    currentTab,
    setCurrentTab,
    createdAgent,
    agentData,
    updateAgentData,
    saveAgent,
    deleteAgent,
    isSavingAgent,
    isDeletingAgent,
    deleteAgentError,
    saveAgentError,
    isLoadingAgent,
    loadAgentError,
    isEditMode,
    canAccessTab,
    resetForm,
  } = useCreateAgent(agentId, setSearchParams);

  // Handle error loading agent
  useEffect(() => {
    if (loadAgentError) {
      toast.error("Failed to load agent data");
      navigate("/admin/agents");
    }
  }, [loadAgentError, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Reset form if not in edit mode when leaving the page
      if (!isEditMode) {
        resetForm();
      }
    };
  }, [isEditMode, resetForm]);

  // Initialize tab from URL params only once on mount
  useEffect(() => {
    if (!isInitialized.current) {
      const tabFromUrl = searchParams.get("tab");
      if (tabFromUrl) {
        const tabNumber = parseInt(tabFromUrl);
        if (tabNumber >= 1 && tabNumber <= 3) {
          setCurrentTab(tabNumber);
        }
      }
      isInitialized.current = true;
    }
  }, [searchParams, setCurrentTab]);

  // Update URL when tab changes (with proper dependency management)
  useEffect(() => {
    if (!isInitialized.current) return; // Skip during initialization

    const tabFromUrl = searchParams.get("tab");
    const currentTabFromUrl = parseInt(tabFromUrl) || 1;

    // Only update URL if the current tab is different from URL
    if (currentTabFromUrl !== currentTab) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("tab", currentTab.toString());
      if (agentId) {
        newParams.set("id", agentId);
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [currentTab, agentId, searchParams, setSearchParams]);

  // Tab definitions
  const tabs = [
    {
      id: 1,
      label: "Settings",
      icon: Settings,
      description: "Configure your agent",
    },
    {
      id: 2,
      label: "Knowledge",
      icon: Brain,
      description: "Add knowledge base",
    },
    {
      id: 3,
      label: "Try AI",
      icon: MessageSquare,
      description: "Test your agent",
    },
  ];

  // Check if tab 3 can be accessed (needs knowledge)
  const canAccessTryAI = canAccessTab(3);

  const handleTabClick = (tabId) => {
    if (tabId === 3 && !canAccessTryAI) {
      toast.error("Add knowledge first to test your agent");
      return;
    }

    if (canAccessTab(tabId) || (tabId === 3 && canAccessTryAI)) {
      setCurrentTab(tabId);
    } else {
      toast.error("Complete previous steps first");
    }
  };

  const handleBack = () => {
    // Reset form state when navigating back
    if (!isEditMode) {
      resetForm();
    }
    navigate("/admin/agents");
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteAgent(agentId);
      toast.success("Agent deleted successfully");
      setShowDeleteModal(false);
      // Navigation handled in hook onSuccess
    } catch (error) {
      toast.error("Failed to delete agent");
      console.error("Delete agent error:", error);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  const isTabAccessible = (tabId) => {
    if (tabId === 3) return canAccessTryAI;
    return canAccessTab(tabId);
  };

  // Loading state for edit mode
  if (isEditMode && isLoadingAgent) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">
            Loading agent data...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4">
            {/* Breadcrumb */}
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Agents</span>
            </button>

            {/* Title and Actions */}
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isEditMode
                    ? `Edit ${createdAgent?.name || "Agent"}`
                    : "Create New Agent"}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  {isEditMode
                    ? "Manage your AI agent configuration"
                    : "Set up your AI agent in 3 simple steps"}
                </p>
              </div>

              {/* Delete Button - only in edit mode */}
              {isEditMode && (
                <button
                  onClick={handleDeleteClick}
                  disabled={isDeletingAgent}
                  className="flex items-center space-x-2 px-4 py-2 text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="text-sm font-medium">
                    {isDeletingAgent ? "Deleting..." : "Delete Agent"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="px-6">
            <nav className="flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = currentTab === tab.id;
                const isAccessible = isTabAccessible(tab.id);

                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    disabled={!isAccessible}
                    className={`
                      flex items-center space-x-2 py-4 px-1 border-b-2 text-sm font-medium transition-colors
                      ${
                        isActive
                          ? "border-blue-500 text-blue-600 dark:text-blue-400"
                          : isAccessible
                          ? "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600"
                          : "border-transparent text-gray-300 dark:text-gray-600 cursor-not-allowed"
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                    {!isAccessible && tab.id > 1 && (
                      <div className="w-2 h-2 bg-gray-300 dark:bg-gray-600 rounded-full" />
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <div className="px-6 py-6">
          {currentTab === 1 && (
            <AgentSettingsTab
              agentData={agentData}
              updateAgentData={updateAgentData}
              saveAgent={saveAgent}
              isSavingAgent={isSavingAgent}
              saveAgentError={saveAgentError}
              createdAgent={createdAgent}
              isEditMode={isEditMode}
            />
          )}

          {currentTab === 2 && (createdAgent || isEditMode || agentId) && (
            <AgentKnowledgeTab
              agentData={createdAgent || agentData}
              agentId={agentId}
            />
          )}

          {currentTab === 3 &&
            (createdAgent || isEditMode || agentId) &&
            canAccessTryAI && (
              <AgentTryAITab agent={createdAgent || agentData} />
            )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Agent"
        message={`Are you sure you want to delete "${
          createdAgent?.name || "this agent"
        }"? This action cannot be undone.`}
        isLoading={isDeletingAgent}
      />
    </div>
  );
};

export default AddAgentPage;
